<template>
  <div class="panel-container">
    <!-- 农事日历 -->
    <div class="panel-card">
      <div class="calendar-header">
        <div class="header-row">
          <h3 class="section-title1">农事日历</h3>
          <div class="header-info">
            <div class="current-stage">
              <span class="stage-label">当前生长阶段:</span>
              <div class="stage-display" :style="getCurrentStageStyle()">
                <img
                  v-if="currentStageInPeriod"
                  :src="getCurrentStageBackground()"
                  class="stage-bg"
                  :alt="currentStageName"
                />
                <span class="stage-text">{{ currentStageName }}</span>
              </div>
            </div>
            <div class="month-selector">
              <span class="month-label">月份</span>
              <el-date-picker
                v-model="selectedMonth"
                type="month"
                style="width: 120px"
                placeholder="选择月份"
                format="yyyy年MM月"
                value-format="yyyy-MM"
                @change="handleMonthChange"
                class="month-picker"
                :picker-options="monthPickerOptions"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="month-title">{{ formatMonthTitle(selectedMonth) }}</div>

      <!-- 日历组件 -->
      <Calendar
        :greenhouseId="greenhouseId"
        :varietyId="varietyId"
        :yearMonth="selectedMonth"
        :currentStageInfo="currentStageInfo"
      />
    </div>
    <div class="panel-card" style="margin-top: 10px">
      <div class="device-control-header">
        <div class="section-title1">智能设备控制</div>
        <div class="device-tabs">
          <div
            class="tab-item"
            :class="{ active: activeDeviceTab === 1 }"
            @click="switchDeviceTab(1)"
          >
            智能灌溉
          </div>
          <div
            class="tab-item"
            :class="{ active: activeDeviceTab === 2 }"
            @click="switchDeviceTab(2)"
          >
            智能控温
          </div>
          <div
            class="tab-item"
            :class="{ active: activeDeviceTab === 3 }"
            @click="switchDeviceTab(3)"
          >
            智能保温
          </div>
          <div
            class="tab-item"
            :class="{ active: activeDeviceTab === 4 }"
            @click="switchDeviceTab(4)"
          >
            智能防雨
          </div>
        </div>
      </div>
      <div class="status-card" v-if="filteredDeviceScenes.length !== 0">
        <!-- 远程状态 -->
        <div class="left" :class="{ activeControl: remoteStatus }">
          {{ remoteStatus ? "• 远程控制" : "• 本地控制" }}
        </div>
        <!-- AI智控状态 -->
        <div class="right">
          <div
            class="method-option"
            :class="{
              active: !aiControlStatus,
              disabled: remoteStatus === false,
            }"
            @click="remoteStatus ? toggleAiControlStatus() : null"
          >
            远程手动
          </div>
          <div
            class="method-option"
            :class="{
              active: aiControlStatus,
              disabled: remoteStatus === false,
            }"
            @click="remoteStatus ? toggleAiControlStatus() : null"
          >
            智能自动
          </div>
        </div>
      </div>
      <div
        class="device-control-container"
        :class="{ 'empty-layout': filteredDeviceScenes.length === 0 }"
      >
        <!-- 空状态显示 -->
        <div v-if="filteredDeviceScenes.length === 0" class="empty-state">
          <div class="empty-icon">📱</div>
          <div class="empty-text">暂无设备数据</div>
          <div class="empty-desc">当前场景下没有可控制的设备</div>
        </div>

        <!-- 横向滑动设备容器 -->
        <div v-else class="device-slider-wrapper">
          <div
            class="device-slider"
            :style="{ transform: `translateX(-${currentSlideIndex * 100}%)` }"
          >
            <!-- 将设备按每4个分组 -->
            <div
              v-for="(deviceGroup, groupIndex) in deviceGroups"
              :key="groupIndex"
              class="device-group"
            >
              <div
                v-for="scene in deviceGroup"
                :key="scene.id"
                class="device-control-item"
              >
                <!-- 灌溉设备 (sceneId = 1) -->
                <div v-if="scene.scene === 1">
                  <div class="device-control-item-header">
                    {{ scene.name }}
                  </div>
                  <div class="switch-box">
                    <span>开启</span>
                    <img
                      :src="getSwitchImage(scene.status)"
                      alt="switch"
                      @click="
                        canUseDownControl() ? toggleDeviceStatus(scene) : null
                      "
                      :class="[
                        'switch-image',
                        { disabled: !canUseDownControl() },
                      ]"
                    />
                    <span>关闭</span>
                  </div>
                </div>
                <!-- 其他设备 (sceneId = 2,3,4) -->
                <div v-else>
                  <div class="device-control-item-header">
                    {{ scene.name }}
                  </div>
                  <div class="btn-box">
                    <div
                      class="control-btn"
                      :class="{
                        active: getDeviceAction(scene) === 'expand',
                        disabled: !canUseDownControl(),
                      }"
                      @click="
                        canUseDownControl()
                          ? setDeviceAction(scene, 'expand')
                          : null
                      "
                    >
                      展开
                    </div>
                    <div
                      class="control-btn"
                      :class="{
                        active: getDeviceAction(scene) === 'pause',
                        disabled: !canUseDownControl(),
                      }"
                      @click="
                        canUseDownControl()
                          ? setDeviceAction(scene, 'pause')
                          : null
                      "
                    >
                      暂停
                    </div>
                    <div
                      class="control-btn"
                      :class="{
                        active: getDeviceAction(scene) === 'collapse',
                        disabled: !canUseDownControl(),
                      }"
                      @click="
                        canUseDownControl()
                          ? setDeviceAction(scene, 'collapse')
                          : null
                      "
                    >
                      收拢
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 指示器 -->
          <div
            v-if="deviceGroups.length > 1"
            class="slider-indicators"
            :style="{
              '--total-slides': deviceGroups.length,
              '--current-slide': currentSlideIndex,
            }"
            @mousedown="startDrag"
            @touchstart="startDrag"
            ref="indicatorContainer"
          >
            <div
              v-for="(group, index) in deviceGroups"
              :key="index"
              class="indicator"
              :class="{ active: currentSlideIndex === index }"
              @click="goToSlide(index)"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { baseUrl } from "@/axios/baseUrl";
import Calendar from "./calendar.vue";

export default {
  components: {
    Calendar,
  },
  name: "CenterPanel",
  props: {
    greenhouseId: {
      type: String,
      default: "",
    },
    greenhouseName: {
      type: String,
      default: "",
    },
    varietyOptions: {
      type: Array,
      default: () => [],
    },
    currentStageInfo: {
      type: Object,
      default: () => ({
        name: "暂无",
        inPeriod: false,
        index: 0,
        allCycleDetailOptions: [],
      }),
    },
  },
  data() {
    return {
      selectedMonth: new Date().toISOString().slice(0, 7), // 当前月份
      monthPickerOptions: {
        disabledDate: (time) => {
          // 禁用当前月份之后的日期
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth();

          const timeYear = time.getFullYear();
          const timeMonth = time.getMonth();

          // 如果年份大于当前年份，禁用
          if (timeYear > currentYear) {
            return true;
          }
          // 如果年份等于当前年份但月份大于当前月份，禁用
          if (timeYear === currentYear && timeMonth > currentMonth) {
            return true;
          }

          return false;
        },
      },
      // 设备场景列表
      deviceScenes: [],
      // 远程状态更新定时器
      remoteStatusTimer: null,
      // 当前激活的设备标签页
      activeDeviceTab: 1,
      isAuto: true,
      // 远程状态 (true-远程, false-本地)
      remoteStatus: true,
      // AI智控状态 (true-自动, false-手动)
      aiControlStatus: true,
      // 当前滑动索引
      currentSlideIndex: 0,
      // 拖动相关状态
      isDragging: false,
      dragStartX: 0,
      indicatorWidth: 0,
      indicatorRect: null, // 新增：存储指示器容器的矩形信息
      // 设备动作状态存储
      deviceActions: {}, // 存储每个设备的当前动作状态
    };
  },
  computed: {
    varietyId() {
      // 从父组件获取当前选中的品种ID
      return this.currentStageInfo.varietyId;
    },

    // 从props获取当前生长时期信息
    currentStageName() {
      return this.currentStageInfo.name || "暂无";
    },
    currentStageInPeriod() {
      return this.currentStageInfo.inPeriod;
    },
    currentStageIndex() {
      return this.currentStageInfo.index;
    },
    // 根据当前激活的标签页过滤设备场景
    filteredDeviceScenes() {
      // 如果设备场景列表为空，直接返回空数组
      if (!this.deviceScenes || this.deviceScenes.length === 0) {
        return [];
      }

      // 查找匹配的场景
      const matchedScene = this.deviceScenes.find(
        (scene) => scene.sceneId === this.activeDeviceTab
      );

      // 如果找到匹配的场景，返回该场景的设备列表，否则返回空数组
      return matchedScene && matchedScene.deviceList
        ? matchedScene.deviceList
        : [];
    },
    // 将设备按每4个分组
    deviceGroups() {
      const devices = this.filteredDeviceScenes;
      const groups = [];
      for (let i = 0; i < devices.length; i += 4) {
        groups.push(devices.slice(i, i + 4));
      }
      return groups;
    },
  },
  watch: {
    currentStageInfo: {
      handler(newVal) {
        // 当当前生长时期信息更新时，不需要额外处理
        // 因为computed会自动更新
      },
      deep: true,
    },
    greenhouseId: {
      handler(newVal) {
        if (newVal) {
          this.getDeviceList(); // 直接从设备列表获取状态
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    // 清理定时器
    if (this.remoteStatusTimer) {
      clearInterval(this.remoteStatusTimer);
      this.remoteStatusTimer = null;
    }

    // 清理拖动事件监听
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.endDrag);
    document.removeEventListener("touchmove", this.onDrag);
    document.removeEventListener("touchend", this.endDrag);
  },
  methods: {
    // 切换设备标签页
    switchDeviceTab(tabId) {
      this.activeDeviceTab = tabId;
      this.currentSlideIndex = 0; // 切换标签页时重置滑动索引
      // 切换标签页时更新状态
      this.updateStatusFromCurrentScene();
    },

    // 跳转到指定滑动页
    goToSlide(index) {
      this.currentSlideIndex = index;
    },

    // 上一页
    prevSlide() {
      if (this.currentSlideIndex > 0) {
        this.currentSlideIndex--;
      }
    },

    // 下一页
    nextSlide() {
      if (this.currentSlideIndex < this.deviceGroups.length - 1) {
        this.currentSlideIndex++;
      }
    },

    // 开始拖动
    startDrag(event) {
      this.isDragging = true;
      const clientX =
        event.type === "touchstart" ? event.touches[0].clientX : event.clientX;
      this.dragStartX = clientX;

      // 获取指示器容器的宽度和位置
      const indicatorElement = this.$refs.indicatorContainer;
      this.indicatorWidth = indicatorElement.offsetWidth;
      this.indicatorRect = indicatorElement.getBoundingClientRect();

      // 添加全局事件监听
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.endDrag);
      document.addEventListener("touchmove", this.onDrag);
      document.addEventListener("touchend", this.endDrag);

      // 阻止默认行为
      event.preventDefault();
    },

    // 拖动中
    onDrag(event) {
      if (!this.isDragging) return;

      const clientX =
        event.type === "touchmove" ? event.touches[0].clientX : event.clientX;

      // 计算相对于指示器容器的位置
      const relativeX = clientX - this.indicatorRect.left;

      // 计算拖动百分比
      const dragPercent = Math.max(
        0,
        Math.min(1, relativeX / this.indicatorWidth)
      );

      // 根据拖动百分比计算目标页面
      const targetSlide = Math.floor(dragPercent * this.deviceGroups.length);
      const newIndex = Math.max(
        0,
        Math.min(this.deviceGroups.length - 1, targetSlide)
      );

      if (newIndex !== this.currentSlideIndex) {
        this.currentSlideIndex = newIndex;
      }

      event.preventDefault();
    },

    // 结束拖动
    endDrag(event) {
      this.isDragging = false;

      // 移除全局事件监听
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.endDrag);
      document.removeEventListener("touchmove", this.onDrag);
      document.removeEventListener("touchend", this.endDrag);
    },

    getCurrentStageStyle() {
      // 与left.vue中的getStageNameStyle保持一致
      const stageIndex = this.currentStageIndex;

      // 计算循环索引
      const getImageIndex = (index) => {
        return index % 8;
      };

      const imageIndex = getImageIndex(stageIndex);

      // 定义不同时期的背景色
      const stageColors = [
        "rgba(212, 249, 215, 0.40)", // 0
        "rgba(140, 230, 143, 0.40)", // 1
        "rgba(0, 218, 167, 0.40)", // 2
        "rgba(0, 218, 167, 0.40)", // 3
        "rgba(218, 58, 0, 0.40)", // 4
        "rgba(218, 156, 0, 0.40)", // 5
        "rgba(218, 116, 0, 0.40)", // 6
        "rgba(0, 127, 218, 0.40)", // 7
      ];

      const baseColor = stageColors[imageIndex];

      if (this.currentStageInPeriod) {
        // 如果在生长期内，使用背景图，不设置背景色
        return {
          textAlign: "center",
          width: "100%",
          height: "34px",
          lineHeight: "34px",
          position: "relative",
          marginBottom: "2px",
        };
      } else {
        // 如果不在生长期内，使用半透明背景色
        return {
          backgroundColor: baseColor,
          textAlign: "center",
          width: "100%",
          height: "34px",
          lineHeight: "34px",
          position: "relative",
          marginBottom: "2px",
        };
      }
    },
    getCurrentStageBackground() {
      const stageIndex = this.currentStageIndex;

      // 计算循环索引
      const getImageIndex = (index) => {
        return index % 8;
      };

      const imageIndex = getImageIndex(stageIndex);

      try {
        return require(`@/assets/img/stage/b${imageIndex}.png`);
      } catch (error) {
        return require("@/assets/img/stage/b0.png");
      }
    },
    handleMonthChange(value) {
      console.log("选择的月份:", value);
      // 这里可以添加月份变化时的处理逻辑
    },

    // 格式化月份标题
    formatMonthTitle(monthString) {
      if (!monthString) return "";

      // 解析月份字符串，格式为 "2025-08"
      const parts = monthString.split("-");
      if (parts.length === 2) {
        const month = parseInt(parts[1]);
        return this.getChineseMonth(month);
      }

      return monthString;
    },

    // 获取中文月份
    getChineseMonth(month) {
      const chineseMonths = [
        "",
        "一月",
        "二月",
        "三月",
        "四月",
        "五月",
        "六月",
        "七月",
        "八月",
        "九月",
        "十月",
        "十一月",
        "十二月",
      ];

      if (month >= 1 && month <= 12) {
        return chineseMonths[month];
      }

      return `${month}月`;
    },

    // 获取设备列表（包含远程状态和AI智控状态）
    getDeviceList() {
      const url = `${baseUrl}/api/device/device-control/device-list`;

      const requestData = {
        greenhouseId: this.greenhouseId,
        remoteStatus: this.remoteStatus,
        aiControlStatus: this.aiControlStatus,
      };

      axios
        .post(url, requestData)
        .then((response) => {
          console.log("设备列表获取成功:", response.data);
          if (response.data) {
            // 更新设备场景列表
            if (
              response.data.deviceScenes &&
              Array.isArray(response.data.deviceScenes)
            ) {
              this.deviceScenes = response.data.deviceScenes;
            } else if (Array.isArray(response.data)) {
              // 兼容原有格式：直接返回设备场景数组
              this.deviceScenes = response.data;
            }
            this.deviceScenes.forEach((scene) => {
              scene.deviceList.forEach((device) => {
                device.status = '';
              });
            });

            // 根据当前激活的场景更新状态
            this.updateStatusFromCurrentScene();

            console.log(
              "远程状态:",
              this.remoteStatus,
              "AI智控状态:",
              this.aiControlStatus
            );
            console.log("设备场景列表:", this.deviceScenes);
          }
        })
        .catch((error) => {
          console.error("获取设备列表失败:", error);
        });
    },

    // 根据当前激活的场景更新状态
    updateStatusFromCurrentScene() {
      if (!this.deviceScenes || this.deviceScenes.length === 0) {
        return;
      }

      // 查找当前激活的场景
      const currentScene = this.deviceScenes.find(
        (scene) => scene.sceneId === this.activeDeviceTab
      );

      if (currentScene) {
        // 更新远程状态和AI智控状态
        if (currentScene.remoteStatus !== undefined) {
          this.remoteStatus = currentScene.remoteStatus;
        }
        if (currentScene.aiControlStatus !== undefined) {
          this.aiControlStatus = currentScene.aiControlStatus;
        }
      }
    },

    // 切换AI智控状态
    toggleAiControlStatus() {
      // 检查是否为本地控制状态，如果是则不允许切换
      if (!this.remoteStatus) {
        this.$message.warning("本地控制状态下无法切换控制模式");
        return;
      }

      const newStatus = !this.aiControlStatus;
      const statusValue = newStatus ? 1 : 0; // 1-开启(自动), 0-关闭(手动)

      // 调用AI智控切换接口
      this.updateAiControlStatus(statusValue, newStatus);
    },

    // 更新AI智控状态到服务器
    updateAiControlStatus(status, newAiControlStatus) {
      const url = `${baseUrl}/api/device/device-control/ai-control`;
      const data = {
        greenhouseId: this.greenhouseId,
        sceneId: this.activeDeviceTab, // 使用当前激活的场景ID
        status: status, // 0-关闭(手动), 1-开启(自动)
      };

      axios
        .post(url, data)
        .then((response) => {
          // 更新本地状态
          this.aiControlStatus = newAiControlStatus;
          // 显示成功提示
          const statusText = newAiControlStatus ? "自动" : "手动";
          this.$message.success(`已切换到${statusText}控制模式`);
        })
        .catch((error) => {
          console.error("AI智控状态更新失败:", error);
          this.$message.error("AI智控状态更新失败，请重试");
        });
    },

    // 根据场景数据生成设备控制数据
    getDeviceControlData(scene) {
      // 获取场景中的第一个设备作为代表设备
      const device =
        scene.deviceList && scene.deviceList.length > 0
          ? scene.deviceList[0]
          : null;

      if (!device) {
        return this.getDefaultDeviceControl(scene.sceneId);
      }

      // 根据场景ID返回对应的设备控制数据结构
      const baseData = {
        id: device.id,
        method: "remote", // 默认远程控制
        status: device.status === 1,
        sceneStatus: "",
        time: null,
        statusTime: null,
        isRemote: true, // 默认远程状态
      };

      // 根据场景ID添加特定字段
      if (scene.sceneId === 1) {
        // 灌溉设备
        return {
          ...baseData,
          type: "drip", // 默认滴灌
          enabled: true,
        };
      } else {
        // 其他设备 (sceneId = 2,3,4)
        return {
          ...baseData,
          curtainAction: "deploy", // 默认展开
        };
      }
    },

    // 获取默认设备控制数据
    getDefaultDeviceControl(sceneId) {
      const baseData = {
        id: null,
        method: "remote",
        status: false,
        sceneStatus: "",
        time: null,
        statusTime: null,
        isRemote: true,
      };

      if (sceneId === 1) {
        // 灌溉设备
        return {
          ...baseData,
          type: "drip",
          enabled: true,
        };
      } else {
        // 其他设备 (sceneId = 2,3,4)
        return {
          ...baseData,
          curtainAction: "deploy",
        };
      }
    },

    // 智能设备控制方法 - 保持原有接口兼容性
    setIrrigationMethod(method) {
      console.log("灌溉控制方式:", method);
      this.$message.success(
        method === "remote" ? "已开启远程手动灌溉模式" : "已开启智能灌溉模式"
      );
    },

    handleIrrigationControl(data) {
      console.log("灌溉控制数据:", data);
    },

    setTemperatureMethod(method) {
      console.log("设备控制方式:", method);
      this.$message.success(
        method === "remote" ? "已开启远程手动控制模式" : "已开启智能控制模式"
      );
    },

    setTemperatureCurtain(action) {
      console.log("设备遮阳帘动作:", action);
    },

    // 获取开关图片 - 修正灌溉设备的显示逻辑
    getSwitchImage(status) {
      console.log("设备场景列表获取开关图片:", status);
      // 灌溉设备特殊逻辑：0关闭，1开启
      return status === 1
        ? require("@/assets/img/stage/swich_open.png") // 1表示开启
        : require("@/assets/img/stage/swich_close.png"); // 0表示关闭
    },

    // 切换设备状态
    toggleDeviceStatus(scene) {
      // 检查是否可以使用下控接口
      if (!this.canUseDownControl()) {
        this.$message.warning("只有远程控制且远程手动模式下才能使用设备控制");
        return;
      }

      // 灌溉设备特殊逻辑：0关闭，1开启
      if (scene.scene === 1) {
        // 灌溉设备：切换状态
        const newStatus = scene.status === 0 ? 1 : 0;

        // 调用下控接口
        this.callDownControlAPI(scene.id, newStatus);

        // 找到对应的场景并更新设备状态
        const sceneIndex = this.deviceScenes.findIndex(
          (s) => s.sceneId === this.activeDeviceTab
        );
        if (sceneIndex !== -1) {
          const deviceIndex = this.deviceScenes[
            sceneIndex
          ].deviceList.findIndex((d) => d.id === scene.id);
          if (deviceIndex !== -1) {
            // 创建新的设备对象来确保响应式更新
            const updatedDevice = {
              ...this.deviceScenes[sceneIndex].deviceList[deviceIndex],
              status: newStatus
            };
            
            // 创建新的设备列表
            const updatedDeviceList = [...this.deviceScenes[sceneIndex].deviceList];
            updatedDeviceList[deviceIndex] = updatedDevice;
            
            // 创建新的场景对象
            const updatedScene = {
              ...this.deviceScenes[sceneIndex],
              deviceList: updatedDeviceList
            };
            
            // 创建新的场景列表
            const updatedScenes = [...this.deviceScenes];
            updatedScenes[sceneIndex] = updatedScene;
            
            // 更新整个设备场景列表
            this.deviceScenes = updatedScenes;
          }
        }
        console.log("更新后的设备场景列表:", this.deviceScenes);

        // 显示状态变化提示
        const statusText = newStatus === 1 ? "开启" : "关闭";
        this.$message.success(`${scene.name} 已${statusText}`);
      }
    },

    // 设置设备动作状态
    setDeviceAction(scene, action) {
      // 检查是否可以使用下控接口
      if (!this.canUseDownControl()) {
        this.$message.warning("只有远程控制且远程手动模式下才能使用设备控制");
        return;
      }

      const deviceId = scene.id;

      // 将动作转换为状态值
      let status;

      switch (action) {
        case "expand":
          status = 0; // 关闭/展开
          break;
        case "collapse":
          status = 1; // 开启/收拢
          break;
        case "pause":
          status = -1; // 暂停
          break;
        default:
          status = 0;
      }

      // 调用下控接口
      this.callDownControlAPI(deviceId, status);

      // 更新本地状态 - 使用 $set 确保响应式更新
      this.$set(this.deviceActions, deviceId, action);

      // 显示状态变化提示
      const actionText = {
        expand: "展开",
        pause: "暂停",
        collapse: "收拢",
      };
      this.$message.success(`${scene.name} 已${actionText[action]}`);
    },

    // 检查是否可以使用下控接口
    canUseDownControl() {
      return this.remoteStatus && !this.aiControlStatus;
    },

    // 调用设备下控接口
    callDownControlAPI(deviceId, status) {
      const url = `${baseUrl}/api/device/device-control/down-control`;
      const data = {
        deviceId: deviceId,
        status: status,
        greenhouseId: this.greenhouseId,
      };

      axios
        .post(url, data)
        .then((response) => {
          console.log("设备下控成功:", response.data);
        })
        .catch((error) => {
          console.error("设备下控失败:", error);
          this.$message.error("设备控制失败，请重试");
        });
    },

    // 获取设备当前动作状态
    getDeviceAction(scene) {
      return this.deviceActions[scene.id] || ""; // 默认返回展开状态
    },
  },
};
</script>

<style lang="less" scoped>
.panel-container {
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100vh - 155px);
}

.month-title {
  width: 100%;
  text-align: center;
  font-size: 24px;
  line-height: 32px;
  font-weight: 800;
  background: linear-gradient(180deg, #ffffff 0%, #95d2f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 5px 0;
}

.panel-card {
  padding: 8px;
  padding-bottom: 2px;
}

.section-title1 {
  height: 26px;
  line-height: 26px;
  font-weight: bold;
  font-size: 20px;
  color: #ffffff;
  text-shadow: 0px 0px 11px #6cc4ff;
  margin-bottom: 6px;
}

.calendar-header {
  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .section-title1 {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .header-info {
    margin: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 30px;
    margin-right: 0;

    .current-stage {
      display: flex;
      align-items: center;
      gap: 8px;

      .stage-label {
        color: #cbd5e1;
        font-size: 14px;
        white-space: nowrap;
      }

      .stage-display {
        position: relative;
        min-width: 100px;
        height: 34px;
        border-radius: 4px;
        overflow: hidden;

        .stage-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
          z-index: 1;
        }

        .stage-text {
          position: relative;
          z-index: 2;
          display: block;
          width: 100%;
          height: 100%;
          line-height: 34px;
          color: #ffffff;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }

    .month-selector {
      display: flex;
      align-items: center;
      gap: 8px;

      .month-label {
        color: #cbd5e1;
        font-size: 14px;
        white-space: nowrap;
      }

      .month-picker {
        :deep(.el-input__inner) {
          background: rgba(0, 11, 47, 0.5);
          border: 1px solid rgba(59, 130, 246, 0.6);
          color: #ffffff;
          border-radius: 4px;
          height: 34px;
          line-height: 34px;
          font-size: 14px;
          padding: 0 10px;
          width: 120px;

          &:focus {
            border-color: rgba(59, 130, 246, 0.8);
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
          }

          &::placeholder {
            color: #9ca3af;
          }
        }

        :deep(.el-input__suffix) {
          color: #ffffff;
        }
      }
    }
  }
}

// 智能设备控制样式
.device-control-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;

  .section-title1 {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .device-tabs {
    display: flex;
    .tab-item {
      padding: 6px 12px;
      font-size: 14px;
      color: #fff;
      border: 1px solid #2680dd;
      background: rgba(2, 21, 35, 0.8);
      cursor: pointer;
      transition: all 0.3s ease;
      white-space: nowrap;

      &.active {
        background: linear-gradient(270deg, #00a1ff 0%, #006aff 100%);
        box-shadow: 0 0 6px rgba(59, 130, 246, 0.3);
      }
    }
  }
}

.device-control-container {
  margin-top: 12px;
  height: 42vh;

  // 空状态时使用 flex 布局
  &.empty-layout {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 44vh;
  }

  // 空状态样式
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    margin-bottom: 85px;
    background: rgba(3, 15, 41, 0.3);
    border-radius: 8px;

    .empty-icon {
      font-size: 48px;
      margin-bottom: 16px;
      opacity: 0.6;
    }

    .empty-text {
      font-size: 18px;
      color: #ffffff;
      font-weight: 500;
      margin-bottom: 8px;
    }

    .empty-desc {
      font-size: 14px;
      color: #9ca3af;
      text-align: center;
    }
  }

  // 横向滑动容器
  .device-slider-wrapper {
    position: relative;
    width: 100%;
    overflow: hidden;
  }

  .device-slider {
    display: flex;
    transition: transform 0.3s ease;
    width: 100%;
  }

  .device-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 10px;
    min-width: 100%;
    flex-shrink: 0;
    width: 100%; // 确保每个组占满容器宽度
  }

  .device-control-item {
    width: 100%; // 改为100%，让grid自动分配宽度
    height: 115px;
    padding: 10px 20px;
    background: url("../../../../assets/img/stage/control.png") no-repeat center
      center;
    background-size: cover; // 确保背景图适应容器

    .device-control-item-header {
      width: 100%;
      font-family: YouSheBiaoTiHei, YouSheBiaoTiHei;
      font-weight: 400;
      font-size: 24px;
      color: #ffffff;
      text-align: left;
    }
    .switch-box {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      font-weight: 400;
      font-size: 20px;
      color: #ffffff;
      margin-top: 17px;
      .switch-image {
        width: 50px;
        height: 29px;
        margin: 0 10px;
        margin-top: 5px;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover:not(.disabled) {
          transform: scale(1.1);
          filter: brightness(1.2);
        }

        &:active:not(.disabled) {
          transform: scale(0.95);
        }

        &.disabled {
          cursor: not-allowed;
          opacity: 0.5;
          filter: grayscale(0.5);
        }
      }
    }
    .btn-box {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-top: 17px;
      width: 100%;

      .control-btn {
        padding: 6px 12px;
        font-size: 16px;
        color: #ffffff;
        background: rgba(38, 128, 221, 0.3);
        border: 1px solid rgba(38, 128, 221, 0.5);
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s ease;
        white-space: nowrap;
        min-width: 50px;
        text-align: center;
        font-weight: 400;

        &:hover:not(.disabled) {
          transform: scale(1.05);
          background: rgba(38, 128, 221, 0.4);
        }

        &.active {
          background: linear-gradient(270deg, #00a1ff 0%, #006aff 100%);
          border-color: rgba(0, 161, 255, 0.8);
          box-shadow: 0 0 6px rgba(0, 161, 255, 0.3);
          color: #ffffff;
        }

        &.disabled {
          cursor: not-allowed;
          opacity: 0.5;
          background: rgba(38, 128, 221, 0.1);
          border-color: rgba(38, 128, 221, 0.2);
        }
      }
    }
  }

  // 指示器样式
  .slider-indicators {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 10px;
    padding: 4px 0;
    position: relative;
    width: 100%;
    height: 4px;
    background: rgba(38, 128, 221, 0.1);
    border-radius: 2px;
    cursor: pointer;
    user-select: none;

    // 进度条背景
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      background: rgba(38, 128, 221, 0.1);
      border-radius: 2px;
      width: 100%;
    }

    // 进度条填充 - 修正为正确的分段显示
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: calc(100% / var(--total-slides) * var(--current-slide));
      height: 100%;
      background: rgba(38, 128, 221, 0.5);
      border-radius: 2px;
      width: calc(100% / var(--total-slides));
      transition: left 0.3s ease;
    }

    // 拖动时的样式
    &:active {
      &::after {
        transition: none; // 拖动时禁用过渡动画
      }
    }

    .indicator {
      width: 8px;
      height: 4px;
      border-radius: 2px;
      background: transparent;
      cursor: pointer;
      transition: all 0.3s ease;
      margin: 0 2px;
      position: relative;
      z-index: 2;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scaleY(1.2);
      }

      &.active {
        background: transparent;
        transform: scaleY(1.2);
      }
    }
  }
}
.status-card {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  .left {
    width: 138px;
    height: 40px;
    background: rgba(3, 15, 41, 0.3);
    box-shadow: inset 0px 0px 9px 0px #cccccc;
    border-radius: 4px 4px 4px 4px;
    font-size: 18px;
    color: #cccccc;
    text-align: center;
    line-height: 40px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }
  .activeControl {
    background: rgba(3, 15, 41, 0.3);
    color: #267fdd;
    box-shadow: inset 0px 0px 9px 0px #267fdd;
  }
  .right {
    display: flex;
    gap: 8px;
    .method-option {
      height: 40px;
      width: 120px;
      line-height: 40px;
      font-size: 20px;
      color: #416595;
      background-color: #34385c;
      font-weight: 400;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover:not(.disabled) {
        transform: scale(1.02);
      }

      &.disabled {
        cursor: not-allowed;
        opacity: 0.4;
        color: #6b7280 !important;
        background-color: #1f2937 !important;
        box-shadow: none !important;

        &:hover {
          transform: none;
        }
      }
    }
    .active {
      color: #ffffff;
      background: linear-gradient(270deg, #00a1ff 0%, #006aff 100%);
      box-shadow: 0 0 6px rgba(59, 130, 246, 0.3);

      &.disabled {
        color: #6b7280 !important;
        background: #1f2937 !important;
        box-shadow: none !important;
        opacity: 0.4;
      }
    }
  }
}
</style>
